import { useState } from "react";
import { Package, ExternalLink } from "lucide-react";
import { DocumentTemplatesPanel } from "@/components/DocumentTemplatesPanel";

// „Vorlage öffnen“ im Beleg (01.11.2026): zeigt GENAU die Liste aus Einstellungen → Angebot/Rechnung-Vorlagen
// (dieselbe Komponente, Auswahl-Modus). Der Beleg bleibt dahinter offen und ungespeichert unverändert.
// onApply(tpl, modus) mit modus "ersetzen" | "anhaengen" übernimmt Positionen und (Häkchen, Standard an) Vor-/Schlusstext (Editor: uebernehmeVorlagePositionen).

const ART = { quote: "Angebot", order: "Auftragsbestätigung", invoice: "Rechnung" };

// Frage „Anhängen oder ersetzen?“ (+ Hinweis bei falscher Belegart); auch vom Beleg selbst genutzt, wenn die Vorlage aus dem eigenen Fenster kommt
export const VorlageFrage = ({ wahl, docType, hatPositionen, onApply, onAbbrechen }) => {
  const passtNicht = wahl.doc_type !== docType;
  const hatTexte = !!(String(wahl.snapshot?.vortext || "").replace(/<[^>]+>/g, "").trim() || String(wahl.snapshot?.schlusstext || "").replace(/<[^>]+>/g, "").trim());
  const [mitTexte, setMitTexte] = useState(true);
  return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="vorlage-frage">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-4 space-y-3">
            <h4 className="font-semibold">Vorlage „{wahl.name}“ übernehmen</h4>
            {passtNicht && (
              <div className="p-2 rounded-sm bg-amber-50 text-amber-900 text-sm border border-amber-200" data-testid="vorlage-hinweis-art">
                Hinweis: Das ist eine Vorlage für {ART[wahl.doc_type] || "ein anderes Dokument"}, Ihr Beleg ist ein {ART[docType]}. Die Positionen lassen sich trotzdem übernehmen.
              </div>
            )}
            {hatPositionen ? (
              <>
                <p className="text-sm">Ihr Beleg hat schon Positionen. Sollen die Positionen der Vorlage angehängt werden oder die vorhandenen ersetzen?</p>
                {Number(wahl.snapshot?.discount) > 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="vorlage-frage-rabatt">
                    Beim Anhängen bleibt der Rabatt Ihres Belegs; beim Ersetzen gilt der Rabatt der Vorlage ({Number(wahl.snapshot.discount).toLocaleString("de-DE", { maximumFractionDigits: 2 })} {(wahl.snapshot.discount_type || "percent") === "percent" ? "%" : "€"}).
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm">Die Positionen der Vorlage werden in Ihren Beleg übernommen.</p>
            )}
            {hatTexte && (
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="vorlage-frage-texte">
                <input type="checkbox" checked={mitTexte} onChange={(e) => setMitTexte(e.target.checked)} className="mt-1 w-4 h-4" data-testid="vorlage-frage-texte-haken" />
                <span>Vor- und Schlusstext der Vorlage übernehmen <span className="text-muted-foreground">(ersetzt die Texte Ihres Belegs)</span></span>
              </label>
            )}
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
              <button onClick={onAbbrechen} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="vorlage-frage-abbrechen">Abbrechen</button>
              {hatPositionen ? (
                <>
                  <button onClick={() => onApply(wahl, "ersetzen", mitTexte)} className="px-4 py-2 text-sm border border-primary text-primary rounded-sm hover:bg-primary/5" data-testid="vorlage-frage-ersetzen">Ersetzen</button>
                  <button onClick={() => onApply(wahl, "anhaengen", mitTexte)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" data-testid="vorlage-frage-anhaengen">Anhängen</button>
                </>
              ) : (
                <button onClick={() => onApply(wahl, "ersetzen", mitTexte)} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" data-testid="vorlage-frage-ok">Übernehmen</button>
              )}
            </div>
          </div>
        </div>
  );
};

export const VorlageOeffnenFenster = ({ docType, hatPositionen, onApply, onClose, onEigenesFenster }) => {
  const [wahl, setWahl] = useState(null);

  const uebernehmen = (tpl) => {
    const passt = tpl.doc_type === docType;
    if (passt && !hatPositionen) { onApply(tpl, "ersetzen", true); return; }
    setWahl(tpl);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-stretch sm:items-center justify-center sm:p-4" data-testid="vorlage-oeffnen-fenster">
        <div className="bg-background sm:rounded-lg shadow-xl w-full sm:max-w-5xl h-full sm:h-[90vh] flex flex-col">
          <div className="p-3 sm:p-4 border-b flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Vorlage öffnen</h3>
              <p className="text-xs text-muted-foreground mt-1">Vorlage ansehen und die Positionen in Ihr {ART[docType] || "Dokument"} übernehmen. Ändern oder löschen können Sie Vorlagen unter Einstellungen.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onEigenesFenster && <button onClick={onEigenesFenster} className="inline-flex items-center gap-1 px-2.5 py-1.5 border rounded-sm text-xs font-medium hover:bg-muted" title="Vorlagen in einem eigenen Fenster öffnen (zweiter Bildschirm)" data-testid="vorlage-eigenes-fenster"><ExternalLink className="w-3.5 h-3.5" /> Eigenes Fenster</button>}
              <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" aria-label="Schließen" data-testid="vorlage-oeffnen-schliessen">✕</button>
            </div>
          </div>
          <div className="p-3 sm:p-4 overflow-y-auto flex-1 min-h-0">
            <DocumentTemplatesPanel variant="embedded" auswahl={{ docType, onUebernehmen: uebernehmen }} />
          </div>
        </div>
      </div>

      {wahl && <VorlageFrage wahl={wahl} docType={docType} hatPositionen={hatPositionen} onApply={onApply} onAbbrechen={() => setWahl(null)} />}
    </>
  );
};
